import { ITaggingFlowOption } from 'interfaces/tagging/ITaggingFlowOption';
import { PitchTypes } from '@trackman/baseball-shared-components';
import { IPlay } from 'models/tagging/IPlay';
import { Play } from 'models/tagging/Play';
import { TaggingSteps } from '../../enums/TaggingSteps';
import { IBreadcrumb } from '../../interfaces/tagging/IBreadcrumb';
import { optionValueType, steps, resumeInterrupted } from './TaggingFlowStateMachineSteps';

interface ITaggingFlowStateInternal {
  stepName: TaggingSteps;
  value: optionValueType;
  displayValue: string;
  interruptedStep?: TaggingSteps;
}

export interface ITaggingFlowState {
  name: TaggingSteps;
  options: ITaggingFlowOption[];
  automaticOptions?: ITaggingFlowOption[];
  hasCatcherThrows: boolean;
  hasBaseRunners: boolean;
  hasFieldingSequence: boolean;
  breadcrumbs: IBreadcrumb[];
  breadcrumbTitle: string;
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => void;
  clearUpdate: (play: Play) => void;
}
export class TaggingFlowStateMachine {
  private previousStates: ITaggingFlowStateInternal[] = [];

  private currentStep: TaggingSteps;

  private interruptedStep?: TaggingSteps;

  private removedData?: ITaggingFlowStateInternal & { index: number };

  private isPlayerTrackingEnabled?: boolean;

  constructor() {
    this.currentStep = TaggingSteps.Pitch;
  }

  public getCurrentState = (): ITaggingFlowState => ({
    name: steps[this.currentStep].name,
    options: steps[this.currentStep].options,
    breadcrumbTitle: steps[this.currentStep].breadcrumbTitle,
    hasCatcherThrows: steps[this.currentStep].hasCatcherThrows,
    hasBaseRunners: steps[this.currentStep].hasBaseRunners,
    hasFieldingSequence: steps[this.currentStep].hasFieldingSequence,
    automaticOptions: steps[this.currentStep].automaticOptions,
    breadcrumbs: this.getBreadcrumbs(),
    updatePlay: steps[this.currentStep].updatePlay,
    clearUpdate: steps[this.currentStep].clearUpdate,
  });

  public abortChangesAndBackToInterruptedStep = (): void => {
    if (this.interruptedStep) {
      this.currentStep = this.interruptedStep;
    }
    if (this.removedData) {
      this.previousStates.splice(this.removedData.index, 0, this.removedData);
    }
  };

  public setIsPlayerTrackingEnabled = (value: boolean) => {
    this.isPlayerTrackingEnabled = value;
  };

  public getPreviousState = (newStep: TaggingSteps) => {
    this.interruptedStep = this.currentStep;
    const state = this.previousStates.find((s) => s.stepName === newStep);

    this.currentStep = newStep;

    if (state) {
      this.removedData = { ...state, index: this.previousStates.indexOf(state) };
      this.previousStates.splice(this.previousStates.indexOf(state), 1);

      return {
        name: steps[newStep].name,
        options: steps[newStep].options,
        breadcrumbTitle: steps[newStep].breadcrumbTitle,
        hasCatcherThrows: steps[newStep].hasCatcherThrows,
        hasBaseRunners: steps[newStep].hasBaseRunners,
        hasFieldingSequence: steps[newStep].hasFieldingSequence,
        automaticOptions: steps[newStep].automaticOptions,
        breadcrumbs: this.getBreadcrumbs(),
        updatePlay: steps[newStep].updatePlay,
        clearUpdate: steps[newStep].clearUpdate,
      };
    }

    return this.getCurrentState();
  };

  public skipPitchTypeStep = () => {
    if (this.previousStates.some((s) => s.stepName === TaggingSteps.Pitch)) {
      return;
    }
    if (this.currentStep === TaggingSteps.Pitch) {
      this.changeState(PitchTypes.Undefined, PitchTypes.Undefined);
    } else {
      this.previousStates.push({
        stepName: TaggingSteps.Pitch,
        value: PitchTypes.Undefined,
        displayValue: PitchTypes.Undefined,
      });
      if (this.interruptedStep === TaggingSteps.Pitch) {
        const newInterruptedStep = steps.Pitch.getNextStep(PitchTypes.Undefined);
        this.interruptedStep = newInterruptedStep !== resumeInterrupted ? newInterruptedStep : undefined;
      }
    }
  };

  public unskipPitchTypeStep = () => {
    if (this.previousStates.length === 1 && this.previousStates[0].stepName === TaggingSteps.Pitch) {
      this.previousStates = [];
      this.currentStep = TaggingSteps.Pitch;
      this.interruptedStep = undefined;
    }
  };

  public changeState = (value: optionValueType, displayValue: string) => {
    if (this.currentStep === TaggingSteps.Final) {
      const runsState = this.previousStates.find((s) => s.stepName === TaggingSteps.Runs);
      if (runsState) {
        runsState.displayValue = displayValue;
        runsState.value = value;
      }

      return {
        newState: this.getCurrentState(),
        updateFunction: steps.Runs.updatePlay,
      };
    }
    this.previousStates.push({
      stepName: this.currentStep,
      value,
      displayValue,
      interruptedStep: this.interruptedStep,
    });
    const newStep = steps[this.currentStep].getNextStep(value, this.isPlayerTrackingEnabled);
    const updateFunction = steps[this.currentStep].updatePlay;

    if (newStep === resumeInterrupted) {
      this.currentStep = this.interruptedStep ?? TaggingSteps.Final;
      this.interruptedStep = undefined;
    } else {
      this.currentStep = newStep;
    }

    return { newState: this.getCurrentState(), updateFunction };
  };

  public interruptState = (newStep: TaggingSteps) => {
    this.interruptedStep = this.currentStep;
    this.currentStep = newStep;
    return this.getCurrentState();
  };

  public backToInterruptedState = () => {
    if (this.interruptedStep) {
      this.currentStep = this.interruptedStep;
    }
  };

  public resetState = (newStep?: TaggingSteps) => {
    const state = this.previousStates.find((s) => s.stepName === newStep);
    if (!state) {
      return {
        newState: this.getCurrentState(),
        updateFunction: (play: IPlay) => {},
      };
    }

    const removedStates = this.previousStates.slice(this.previousStates.indexOf(state), this.previousStates.length);
    this.previousStates = this.previousStates.slice(0, this.previousStates.indexOf(state));
    this.interruptedStep = state.interruptedStep;
    this.currentStep = state.stepName;

    const undoUpdates = removedStates.map((r) => steps[r.stepName].clearUpdate);
    return {
      newState: this.getCurrentState(),
      updateFunction: (play: IPlay) => {
        undoUpdates.map((u) => u(play));
      },
    };
  };

  public exportData = () => {
    const obj = {
      previousStates: this.previousStates,
      currentStep: this.currentStep,
      interruptedStep: this.interruptedStep,
    };
    return JSON.stringify(obj);
  };

  public importData = (data: string) => {
    if (!data) {
      return;
    }
    const { previousStates, currentStep, interruptedStep } = JSON.parse(data) ?? {};
    this.previousStates = previousStates || [];
    this.currentStep = currentStep || TaggingSteps.Pitch;
    this.interruptedStep = interruptedStep;
  };

  private getBreadcrumbs = () => {
    return this.previousStates.map((s) => ({
      display: s.displayValue,
      step: s.stepName,
    }));
  };
}
